import type {
  ReportData,
  NewReportRequest,
  Ngrams,
  ReportPDFProps,
  RenameReportPayload,
  MergeReportsPayload,
  FetchMergeableReportsParams,
  MergeableReportsResponse,
  BatchReportRequest,
} from "~/types/global";
import { REPORTS_CLIENT } from "../clients/reports.client";
import { ListItem, ListReportOutputDTO } from "root/domain/http/dto/report/list.report.output";
import { MergeableReportsOutputDTO } from "root/domain/http/dto/report/list.merge.reports.output";
import { SummaryOutputDTO } from "root/domain/http/dto/report/summary.output";
import {
  GenerateSummaryInputDTO,
  UpdateSummaryInputDTO,
  type UpdateSummaryInput
} from "root/domain/http/dto/report/summary.input";
import { REPORT_CONSTANTS } from "~/helpers/constants";
import { parseFilenameFromContentDisposition } from "~/helpers/parse-content-disposition.helper";

interface FetchReportListParams {
  hmacFilters?: string[] | undefined;
  page?: number;
  limit?: number;
  folderId?: string | null;
}

export const fetchReportList = async ({
  hmacFilters,
  page = 1,
  limit = 50,
  folderId = null
}: FetchReportListParams = {}): Promise<ReadonlyArray<ListItem>> => {
  try {
    const filterParam = REPORT_CONSTANTS.report_list_params.hmac_filter;
    let url = '/get-saved-reports';
    const queryParams = [`page=${page}`, `limit=${limit}`];  // paginação

    if (folderId) {
      queryParams.push(`folder_id=${folderId}`);
    }

    // filtros de busca
    if (hmacFilters && hmacFilters.length > 0) {
      console.log("hmacFilters: ", hmacFilters);
      hmacFilters.forEach(filter => {
        queryParams.push(`${filterParam}=${encodeURIComponent(filter)}`);
      });
    }

    // combinar params
    if (queryParams.length > 0) {
      url = `${url}?${queryParams.join('&')}`;
    }

    console.log(`Fetching reports with URL: ${url}`);
    const response = await REPORTS_CLIENT.get<ListItem[]>(url);
    const dataList = new ListReportOutputDTO(response.data);
    return dataList.getAllItems();
  } catch (error) {
    console.error("Error fetching reports list data:", error);
    throw error;
  }
};

// TODO - criar domain/http/dto/detail.reports.output.ts DetailsReportsOutputDTO
export const fetchReportById = async (id: string) => {
  try {
    const response = await REPORTS_CLIENT.get(`/get-one-report/${id}`);
    const data = await response.data;
    const result = data
    console.log("result: ", result);
    return result;
  } catch (error) {
    console.error("Error fetching report:", error);
    throw error;
  }
};

// TODO - criar domain/http/dto/create.reports.output.ts CreateReportsOutputDTO
export const createNewReport = async (payload: NewReportRequest) => {
  try {
    const response = await REPORTS_CLIENT.post(`/get-data-from-snap-api`, payload);
    if (!response?.data) {
      console.log("createNewReport response: ", response)
    }
    const data = response?.data as { id: string, credits_minimun: number };
    console.log("createNewReport data: ", data)
    return data;
  } catch (error) {
    console.error("createNewReport Error creating new report:", error);
    throw error;
  }
};

// TODO - criar domain/http/dto/add.reports.output.ts AddReportsOutputDTO
export const addNewReport = async (report: ReportData, id: string) => {
  try {
    const response = await REPORTS_CLIENT.put(`/insert-report/${id}`, report);
    return response.data;
  } catch (error) {
    console.log("addNewReport Error adding new report:", error);

    throw error;
  }
};

// TODO - adicionar endpoint real para filtro
export const getSearchedReports = async (searchTerm: Ngrams) => {
  try {
    console.log("getSearchedReports searchTerm:", searchTerm);
    const response = await REPORTS_CLIENT.post(`/get-saved-reports`, searchTerm);
    const reportList = new ListReportOutputDTO(response.data).getReports();
    return reportList;
  } catch (error) {
    console.error("Error searching reports:", error);
    throw error;
  }
};

export const deleteReport = async (id: string) => {
  try {
    const response = await REPORTS_CLIENT.delete(`/delete-report/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting report:", error);
    throw error;
  }
};

export const generatePDF = async (props: ReportPDFProps): Promise<{ url: string; filename?: string }> => {
  try {
    const response = await REPORTS_CLIENT.post('/create/pdf', props, {
      responseType: 'blob'
    });
    const pdfBlob = response.data;
    const url = URL.createObjectURL(pdfBlob as Blob);
    const filename = parseFilenameFromContentDisposition(
      response.headers?.['content-disposition'] ?? response.headers?.['Content-Disposition'],
    );
    return { url, filename };
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
}

export const renameReport = async (payload: RenameReportPayload) => {
  try {
    const response = await REPORTS_CLIENT.patch(`/rename-report/${payload[REPORT_CONSTANTS.new_report.report_id]}`, payload);
    return response.data;
  } catch (error) {
    console.error("Error renaming report:", error);
    throw error;
  }
};

export const mergeReports = async (payload: MergeReportsPayload) => {
  try {
    const response = await REPORTS_CLIENT.post(`/merge-reports`, payload);
    return response.data;
  } catch (error) {
    console.error("Error merging reports:", error);
    throw error;
  }
}

export const fetchMergeableReports = async ({
  report_id,
  hmacFilters,
  page = 1,
  limit = 50,
}: FetchMergeableReportsParams): Promise<MergeableReportsOutputDTO> => {
  try {
    const filterParam = REPORT_CONSTANTS.report_list_params.hmac_filter;
    let url = `/get-mergeable-reports/${report_id}`;
    const queryParams = [`page=${page}`, `limit=${limit}`];

    // filtros de busca
    if (hmacFilters && hmacFilters.length > 0) {
      console.log("hmacFilters: ", hmacFilters);
      hmacFilters.forEach((filter: string) => {
        queryParams.push(`${filterParam}=${encodeURIComponent(filter)}`);
      });
    }

    // combinar params
    if (queryParams.length > 0) {
      url = `${url}?${queryParams.join('&')}`;
    }

    console.log(`Fetching mergeable reports with URL: ${url}`);
    const response = await REPORTS_CLIENT.get<MergeableReportsResponse>(url);
    return new MergeableReportsOutputDTO(response.data);
  } catch (error) {
    console.error("Error fetching mergeable reports:", error);
    throw error;
  }
};

export const createBatchReport = async (payload: BatchReportRequest) => {
  try {
    const response = await REPORTS_CLIENT.post(`/batch-create`, payload);
    return response.data;
  } catch (error) {
    console.error("Error creating batch report:", error);
    throw error;
  }
}

export const generateSummaryRequest = async (payload: {
  report_id: string;
  data: any;
  ai_model?: string;
  user_prompt?: string;
  report_sources_data?: any[];
  report_sources_used?: any[];
}) => {
  try {
    const inputDTO = new GenerateSummaryInputDTO(payload.report_id, payload.data, payload.ai_model, payload.user_prompt, payload.report_sources_data, payload.report_sources_used);
    console.log("[SUMARIO] - payload enviado: ", inputDTO.toJSON())
    const response = await REPORTS_CLIENT.post("/summary", inputDTO.toJSON());
    return response.data;
  } catch (error) {
    console.error("Error generating resume:", error);
    throw error;
  }
};

export const updateSummaryRequest = async (
  reportId: string,
  summaryPayload: UpdateSummaryInput,
) => {
  try {
    const inputDTO = new UpdateSummaryInputDTO(summaryPayload);
    const response = await REPORTS_CLIENT.put(
      `/summary/${reportId}`,
      inputDTO.toJSON(),
    );
    return response.data;
  } catch (error) {
    console.error("Error updating summary:", error);
    throw error;
  }
};

export const getSummaryRequest = async (reportId: string) => {
  try {
    const response = await REPORTS_CLIENT.get(`/summary/${reportId}`);
    return new SummaryOutputDTO(response.data);
  } catch (error) {
    console.error("Error fetching summary:", error);
    throw error;
  }
};
